import { useState, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core'
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { EmptyState } from '@/components/EmptyState'
import { Plus, Mail } from 'lucide-react'
import { StepCard } from './StepCard'
import { StepForm } from './StepForm'
import type { SequenceStep, SequenceStepCreate, SequenceStepUpdate } from '@/types/api'

interface StepsEditorProps {
  sequenceId: string
  signatureHtml?: string
  steps: SequenceStep[]
  isLoading: boolean
  onCreateStep: (data: SequenceStepCreate) => void
  onUpdateStep: (stepId: string, data: SequenceStepUpdate) => void
  onDeleteStep: (stepId: string) => void
  onReorderSteps: (stepIds: string[]) => void
}

export function StepsEditor({
  sequenceId,
  signatureHtml,
  steps,
  isLoading,
  onCreateStep,
  onUpdateStep,
  onDeleteStep,
  onReorderSteps,
}: StepsEditorProps) {
  const { t } = useTranslation('sequences')
  const [editingStep, setEditingStep] = useState<string | null>(null)
  const [showNewForm, setShowNewForm] = useState(false)
  const stepIds = useMemo(() => steps.map((s) => s.id), [steps])

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  )

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event
    if (!over || active.id === over.id) return

    const oldIndex = steps.findIndex((s) => s.id === active.id)
    const newIndex = steps.findIndex((s) => s.id === over.id)
    if (oldIndex === -1 || newIndex === -1) return

    const reordered = [...steps]
    const [moved] = reordered.splice(oldIndex, 1)
    reordered.splice(newIndex, 0, moved)
    onReorderSteps(reordered.map((s) => s.id))
  }

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <div>
          <CardTitle>{t('stepsEditor.title')}</CardTitle>
          <CardDescription>
            {t('stepsEditor.description')}
          </CardDescription>
        </div>
        <Button onClick={() => setShowNewForm(true)} disabled={showNewForm}>
          <Plus className="h-4 w-4 mr-2" />
          {t('stepsEditor.addStep')}
        </Button>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="space-y-4">
            <Skeleton className="h-32 w-full" />
            <Skeleton className="h-32 w-full" />
          </div>
        ) : (
          <div className="space-y-4">
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={handleDragEnd}
            >
              <SortableContext
                items={stepIds}
                strategy={verticalListSortingStrategy}
              >
                {steps.map((step, index) => (
                  <StepCard
                    key={step.id}
                    step={step}
                    stepNumber={index + 1}
                    sequenceId={sequenceId}
                    signatureHtml={signatureHtml}
                    firstStepSubject={steps[0]?.subject}
                    isEditing={editingStep === step.id}
                    onEdit={() => setEditingStep(step.id)}
                    onCancel={() => setEditingStep(null)}
                    onSave={(data) => {
                      onUpdateStep(step.id, data)
                      setEditingStep(null)
                    }}
                    onDelete={() => onDeleteStep(step.id)}
                  />
                ))}
              </SortableContext>
            </DndContext>

            {showNewForm && (
              <StepForm
                stepNumber={steps.length + 1}
                firstStepSubject={steps[0]?.subject}
                initialData={{
                  subject: '',
                  body_html: '',
                  delay_days: steps.length > 0 ? 1 : 0,
                  delay_hours: 0,
                }}
                onSave={(data) => {
                  onCreateStep(data)
                  setShowNewForm(false)
                }}
                onCancel={() => setShowNewForm(false)}
                isNew
              />
            )}

            {steps.length === 0 && !showNewForm && (
              <EmptyState
                icon={Mail}
                title={t('stepsEditor.empty.title')}
                description={t('stepsEditor.empty.description')}
                action={{
                  label: t('stepsEditor.empty.action'),
                  onClick: () => setShowNewForm(true),
                }}
              />
            )}
          </div>
        )}
      </CardContent>
    </Card>
  )
}
